// FILE: src/components/student/SupportChannelsCard.jsx
// The three support channels with contact details and hours, on the course
// homepage (MTO 10.1, 12.2): technical, student service and course content.
// Details come from GET /api/public/support-channels.
import React, { useEffect, useState } from "react";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { Link as RouterLink } from "react-router-dom";
import { apiFetch } from "../../services/api";
import { Section } from "../ui";
import { ticketCategoryHint } from "../../i18n/labels";

const ICONS = { TECHNICAL: ComputerOutlinedIcon, SERVICE: BadgeOutlinedIcon, CONTENT: MenuBookOutlinedIcon };

export function useAllSupportChannels() {
  const [channels, setChannels] = useState(null);
  useEffect(() => {
    let cancelled = false;
    apiFetch("/api/public/support-channels")
      .then((res) => (res.ok ? res.json() : { channels: [] }))
      .then((data) => !cancelled && setChannels(data.channels || []))
      .catch(() => !cancelled && setChannels([]));
    return () => {
      cancelled = true;
    };
  }, []);
  return channels;
}

export function ChannelBlock({ channel }) {
  const Icon = ICONS[channel.type] || ComputerOutlinedIcon;
  return (
    <Box sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%" }} data-testid={`support-channel-${channel.type}`}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "center", mb: 1 }}>
        <Icon aria-hidden sx={{ color: "primary.main" }} />
        <Typography component="h3" sx={{ fontWeight: 650 }}>
          {channel.name}
        </Typography>
      </Stack>
      {ticketCategoryHint[channel.type] && (
        <Typography sx={{ color: "text.secondary", mb: 1 }}>{ticketCategoryHint[channel.type]}</Typography>
      )}
      <Stack spacing={0.5}>
        {channel.phone && (
          <Typography>
            Phone: <Link href={`tel:${channel.phone.replace(/[^\d+]/g, "")}`}>{channel.phone}</Link>
          </Typography>
        )}
        {channel.email && (
          <Typography sx={{ overflowWrap: "anywhere" }}>
            Email: <Link href={`mailto:${channel.email}`}>{channel.email}</Link>
          </Typography>
        )}
        {channel.hours && <Typography>Hours: {channel.hours}</Typography>}
        {!channel.phone && !channel.email && (
          <Typography sx={{ color: "text.secondary" }}>Use “Ask for help” to send a request; we reply within 48 hours.</Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function SupportChannelsCard({ showRequestButton = true }) {
  const channels = useAllSupportChannels();
  return (
    <Section
      id="support-channels"
      title="Get help"
      description="Three kinds of support, each with its own contact. We answer every request within 48 hours."
      action={
        showRequestButton ? (
          <Button component={RouterLink} to="/student/support" variant="outlined" sx={{ flexShrink: 0 }}>
            Ask for help
          </Button>
        ) : null
      }
    >
      {channels === null ? (
        <Typography sx={{ color: "text.secondary" }}>Loading support contact details…</Typography>
      ) : channels.length === 0 ? (
        <Typography>Support contact details are unavailable right now. Use “Ask for help” to send a request.</Typography>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
          {channels.map((c) => (
            <ChannelBlock key={c.type} channel={c} />
          ))}
        </Box>
      )}
    </Section>
  );
}
